class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return; // guard: header markup not yet present/duplicated, avoid crashing cart drawer init
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : (document.getElementById('CartDrawer') || this.querySelector('.drawer__inner'));
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    if (!parsedState || parsedState.status || !parsedState.sections) {
      console.warn('[CartDrawer] renderContents received an unusable response, skipping render.', parsedState);
      return;
    }

    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) {
        console.warn(`[CartDrawer] Missing section "${section.id}" in response, skipping.`);
        return;
      }
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
        
      if (!sectionElement) {
        console.warn(`[CartDrawer] No DOM element found for selector "${section.selector || section.id}", skipping.`);
        return;
      }
      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHTML !== null) sectionElement.innerHTML = innerHTML;
    });

    setTimeout(() => {
      const newOverlay = this.querySelector('#CartDrawer-Overlay');
      if (newOverlay) {
        newOverlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    
    // Try target selector first
    let target = selector ? parsed.querySelector(selector) : null;
    
    // Fallback 1: try .shopify-section
    if (!target) {
      target = parsed.querySelector('.shopify-section');
    }
    
    // Fallback 2: grab body's first element or entire body
    if (!target) {
      target = parsed.body.firstElementChild || parsed.body;
    }

    return target ? target.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: 'cart-drawer', // Targets <cart-drawer> element itself
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Size / Qty pill dropdowns inside the cart drawer (custom <details> based
// dropdown, not a native <select>, so it looks and behaves the same on iOS
// and Android). Attached once at document level so it keeps working after
// drawer.renderContents() replaces the drawer markup.
if (!window.__cartItemPillDropdownsBound) {
  window.__cartItemPillDropdownsBound = true;

  // Only one pill dropdown open at a time.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.classList || !details.classList.contains('cart-item__pill-dropdown')) return;
      if (!details.open) return;

      document.querySelectorAll('.cart-item__pill-dropdown[open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    },
    true
  );

  // Close any open pill dropdown when clicking/tapping outside it.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.cart-item__pill-dropdown[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
}